/**
 * Helpers for building Daily Menu Screen URLs.
 * URL format: /daily-menu-screen/:customerCode/:locationCode/:orientation?env=xxx
 * Menu sa určuje na backende podľa customerCode (bez menuId v URL).
 * Jazyk a interval rotácie sa berú z nastavení na obrazovke, nie z URL.
 */

const DEFAULT_LANGUAGE_SHORT = "sk";

/**
 * Vráti backend base URL podľa env (dev | stage | prod).
 * @param {string} env - "dev" | "stage" | "prod" alebo ľubovoľný reťazec (default prod)
 * @returns {string} base URL bez koncového lomítka
 */
export function getBackendBaseUrlFromEnv(env) {
  const e = typeof env === "string" ? env.trim().toLowerCase() : "";
  if (e === "dev") return "http://localhost:8080";
  if (e === "stage") return "https://staging.gastrokiosk.online";
  if (e === "prod") return "https://be.gastrokiosk.online";
  return "https://be.gastrokiosk.online";
}

/**
 * Converts a full locale code to short form for URL (e.g. "sk-SK" → "sk", "en-US" → "en").
 * @param {string} full - e.g. "sk-SK", "en-US"
 * @returns {string} e.g. "sk", "en"
 */
function localeToShort(full) {
  if (!full || typeof full !== "string") return DEFAULT_LANGUAGE_SHORT;
  const trimmed = full.trim();
  const dash = trimmed.indexOf("-");
  return dash > 0 ? trimmed.slice(0, dash) : trimmed;
}

/**
 * Builds the languages param for the URL in short format only (sk, en, not sk-SK or en-US).
 * - If settings.languages is an object: keys (full locale) where value is truthy, sorted, converted to short, joined by "_".
 * - If settings.languages is a string (e.g. "sk-SK_en-US"): split by "_", convert each to short, join by "_".
 * - If missing or empty: return "sk".
 * @param {Object} settings - Daily menu settings (dsokDailyMenu.settings)
 * @returns {string} e.g. "sk" or "sk_en"
 */
export function getDailyMenuLanguagesParam(settings) {
  if (!settings) return DEFAULT_LANGUAGE_SHORT;
  const raw = settings.languages;
  let fullCodes = [];
  if (typeof raw === "string" && raw.trim()) {
    fullCodes = raw.split("_").map((s) => s.trim()).filter(Boolean);
  } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    fullCodes = Object.keys(raw)
      .filter((k) => raw[k])
      .sort();
  }
  if (fullCodes.length === 0) return DEFAULT_LANGUAGE_SHORT;
  const shortCodes = fullCodes.map(localeToShort);
  const seen = new Set();
  const unique = shortCodes.filter((s) => {
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });
  return unique.join("_");
}

/**
 * Returns the number of enabled languages (for rotation logic).
 * @param {Object} settings
 * @returns {number}
 */
function getEnabledLanguagesCount(settings) {
  if (!settings) return 0;
  const param = getDailyMenuLanguagesParam(settings);
  return param.split("_").filter(Boolean).length;
}

/**
 * Rotation interval in milliseconds for the screen URL.
 * - If languageRotationEnabled === true AND >= 2 languages: settings.languageRotationIntervalSec * 1000
 * - Else: 0 (screen will not rotate)
 * @param {Object} settings - Daily menu settings
 * @returns {number} milliseconds
 */
export function getDailyMenuRotationIntervalMs(settings) {
  if (!settings) return 0;
  const enabled = settings.languageRotationEnabled === true;
  const count = getEnabledLanguagesCount(settings);
  if (enabled && count >= 2) {
    const sec = Number(settings.languageRotationIntervalSec);
    if (!Number.isFinite(sec) || sec < 0) return 0;
    return Math.round(sec * 1000);
  }
  return 0;
}

/**
 * Builds the Daily Menu Screen path (without origin). Env sa predáva ako query parameter ?env=xxx.
 * @returns {string} Path e.g. "daily-menu-screen/62/main/landscape" or "" if missing required params
 */
export function getDailyMenuScreenPath({
  customerCode = "",
  locationCode = "",
  env,
  settings,
  menuId,
  orientation = "landscape",
}) {
  const safeCustomer = String(customerCode ?? "").trim();
  const safeLocation = String(locationCode ?? "").trim();
  if (!safeCustomer || !safeLocation) return "";

  const safeOrientation =
    orientation === "portrait" ? "portrait" : "landscape";

  return ["daily-menu-screen", safeCustomer, safeLocation, safeOrientation].join(
    "/",
  );
}

/**
 * Builds the full Daily Menu Screen URL.
 * @param {Object} params
 * @param {string} [params.origin] - e.g. window.location.origin
 * @param {string} [params.customerCode]
 * @param {string} [params.locationCode]
 * @param {string} [params.env] - dev | stage | prod
 * @param {Object} [params.settings] - Daily menu settings (for languages + rotation)
 * @param {string} [params.menuId] - (ignored) menu sa určuje na backende
 * @param {string} [params.orientation] - "landscape" | "portrait"
 * @returns {string} Full URL or "" if required params missing
 */
export function buildDailyMenuScreenUrl({
  origin = "",
  customerCode = "",
  locationCode = "",
  env = "prod",
  settings,
  menuId = "default",
  orientation = "landscape",
  template,
}) {
  const path = getDailyMenuScreenPath({
    customerCode,
    locationCode,
    settings,
    menuId,
    orientation,
  });
  if (!path) return "";
  // template = id vizuálnej šablóny (napr. "template_1"); ak nie je zadaný priamo,
  // zoberie sa z nastavení (settings.template).
  const templateId = String(
    template ?? settings?.template ?? "",
  ).trim();
  let search = `?env=${encodeURIComponent(env)}`;
  if (templateId) {
    search += `&template=${encodeURIComponent(templateId)}`;
  }
  const hasOrigin = typeof origin === "string" && origin.length > 0;
  const base = hasOrigin ? `${origin.replace(/\/$/, "")}/${path}` : `/${path}`;
  return base + search;
}
